import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Sparkles, Phone, ChevronRight } from "lucide-react";
import { BRAND } from "@/config/brand";
import { CLUSTERS, ARTICLES } from "@/config/articles";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

export default function ArticlesIndex() {
  useEffect(() => {
    document.title = "Learn — Small-Business Growth Guides | PlugitAI";
    const set = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    set("description", "Straight-talk guides for local business owners on missed calls, empty calendars, website facelifts, AI twins, and how to stop being the bottleneck in your own business.");
    set("robots", "index,follow");
  }, []);

  return (
    <div className="min-h-screen w-full" style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid="articles-index">
      <TopNav />
      <section className="relative z-10 pt-28 md:pt-40 pb-20">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Learn</span>
          </nav>

          <div className="max-w-[820px] mb-14">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>PlugitAI · Learn</div>
            <h1 className="font-black tracking-[-0.02em] leading-[1.02]" style={{ fontSize: "clamp(2.2rem, 4.4vw, 4rem)" }}>
              Straight-talk guides for{" "}
              <span style={{
                backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>local business owners.</span>
            </h1>
            <p className="mt-4 text-lg" style={{ color: C.textDim }}>
              No jargon. No AI buzzwords. Just what actually works when the phone is ringing off the hook,
              the calendar has holes, or your website hasn&apos;t brought you a lead in six months.
            </p>
          </div>

          {/* By cluster */}
          {CLUSTERS.map((cluster) => {
            const items = ARTICLES.filter((a) => a.cluster === cluster.id);
            if (!items.length) return null;
            return (
              <div key={cluster.id} className="mb-16" data-testid={`articles-cluster-${cluster.id}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-2 h-2 rounded-full" style={{ background: cluster.accent, boxShadow: `0 0 10px ${cluster.accent}` }} />
                  <h2 className="text-xs uppercase tracking-[0.22em] font-black" style={{ color: cluster.accent }}>{cluster.label}</h2>
                  <span className="text-xs" style={{ color: C.textFade }}>· {items.length} article{items.length !== 1 && "s"}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((a) => (
                    <Link key={a.slug} to={`/learn/${a.slug}`}
                          className="group rounded-2xl p-5 md:p-6 border transition-all hover:-translate-y-1"
                          style={{
                            borderColor: C.line,
                            background: "rgba(17,24,39,0.55)",
                            backdropFilter: "blur(10px)",
                            boxShadow: `0 30px 80px -30px ${cluster.accent}44`,
                          }}
                          data-testid={`article-card-${a.slug}`}>
                      <div className="text-[9px] uppercase tracking-[0.22em] mb-3 flex items-center gap-1.5" style={{ color: cluster.accent }}>
                        <Sparkles size={11} /> {cluster.label}
                      </div>
                      <div className="font-bold text-white text-base md:text-lg leading-tight">{a.title}</div>
                      <p className="mt-2 text-xs md:text-sm leading-relaxed" style={{ color: C.textDim }}>{a.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between text-xs" style={{ color: C.textFade }}>
                        <span className="flex items-center gap-1"><Clock size={11} /> {a.readMinutes} min</span>
                        <span className="flex items-center gap-1" style={{ color: C.blueGlow }}>
                          Read <ArrowUpRight size={12} />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <PlugitAIChat />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
